
import { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MapPin, GraduationCap, ChevronRight, CheckCircle } from 'lucide-react';

interface RegistrationData {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  educationLevel: string;
}

interface RegistrationFormProps {
  onRegister: (data: RegistrationData) => void;
  currentStep: number;
}

const EDUCATION_LEVELS = [
  { value: 'beginner', label: 'Beginner', description: 'New to computers' },
  { value: 'ordinary', label: 'Ordinary Level', description: 'O-Level graduate' },
  { value: 'advanced', label: 'Advanced Level', description: 'A-Level graduate' },
  { value: 'other', label: 'Other', description: 'Other qualification' },
];

const RegistrationForm: React.FC<RegistrationFormProps> = ({ onRegister, currentStep }) => {
  const [formData, setFormData] = useState<RegistrationData>({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    educationLevel: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\+?[0-9]{10,15}$/.test(formData.phone.replace(/\s/g, ''))) newErrors.phone = 'Invalid phone number';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.educationLevel) newErrors.educationLevel = 'Please select education level';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onRegister(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const inputFields = [
    {
      name: 'fullName',
      label: 'Full Name',
      type: 'text',
      placeholder: 'Enter your full name',
      icon: User,
    },
    {
      name: 'email',
      label: 'Email Address',
      type: 'email',
      placeholder: 'example@email.com',
      icon: Mail,
    },
    {
      name: 'phone',
      label: 'Phone Number',
      type: 'tel',
      placeholder: '+256 7XX XXX XXX',
      icon: Phone,
    },
    {
      name: 'location',
      label: 'Location (District/City)',
      type: 'text',
      placeholder: 'e.g., Kampala, Wakiso',
      icon: MapPin,
    },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-red-600 p-8 text-white">
          <div className="flex items-center gap-3 mb-3">
            <GraduationCap className="w-8 h-8" />
            <h2 className="text-3xl font-bold">Student Registration</h2>
          </div>
          <p className="text-blue-100 text-lg">
            Join our learning community and start your journey to digital literacy
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-8">
          {/* Personal Information */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Personal Information
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {inputFields.map((field, index) => (
                <motion.div
                  key={field.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="space-y-2"
                >
                  <label className="block text-sm font-semibold text-gray-700">
                    {field.label}
                  </label>
                  <div className="relative">
                    <field.icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={field.type}
                      name={field.name}
                      value={formData[field.name as keyof RegistrationData]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      className={`w-full pl-12 pr-4 py-3.5 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                        errors[field.name]
                          ? 'border-red-400 bg-red-50'
                          : 'border-gray-200 hover:border-blue-300 bg-gray-50'
                      }`}
                    />
                  </div>
                  {errors[field.name] && (
                    <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Education Level */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600" />
              Education Level
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {EDUCATION_LEVELS.map((level, index) => (
                <motion.button
                  key={level.value}
                  type="button"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, educationLevel: level.value }));
                    if (errors.educationLevel) {
                      setErrors(prev => ({ ...prev, educationLevel: '' }));
                    }
                  }}
                  className={`p-4 rounded-xl border-2 text-center transition-all duration-300 ${
                    formData.educationLevel === level.value
                      ? 'border-blue-500 bg-blue-50 shadow-lg scale-105'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
                    formData.educationLevel === level.value
                      ? 'bg-gradient-to-br from-blue-600 to-red-600'
                      : 'bg-gray-100'
                  }`}>
                    <GraduationCap className={`w-6 h-6 ${
                      formData.educationLevel === level.value ? 'text-white' : 'text-gray-600'
                    }`} />
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1">{level.label}</p>
                  <p className="text-xs text-gray-500">{level.description}</p>
                </motion.button>
              ))}
            </div>
            {errors.educationLevel && (
              <p className="text-red-500 text-sm mt-2">{errors.educationLevel}</p>
            )}
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-red-600 text-white rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
          >
            Continue to Skills Selection
            <ChevronRight className="w-5 h-5" />
          </motion.button>

          {currentStep >= 2 && (
            <div className="mt-4 text-center">
              <span className="inline-flex items-center gap-2 text-green-600 font-medium">
                <CheckCircle className="w-5 h-5" />
                Registration Complete - Step 1 Done
              </span>
            </div>
          )}
        </form>
      </motion.div>
    </div>
  );
};

export default RegistrationForm;
